import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { get } from "../../utils/api";
import styles from "./SectionA.module.css";
import ReportTable from "./ReportTable";
import useReportingYearLabels from "./SectionCPrinciples/useReportingYearLabels";

const SectionA = () => {
  const { id } = useParams();
  const { currentYear: brsrFyCurrent, previousYear: brsrFyPrevious } =
    useReportingYearLabels();
  const [loading, setLoading] = useState(false);
  const [sectionAData, setSectionAData] = useState(null);

  useEffect(() => {
    if (!id) return;
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await get(
        `/brsr/get/sections?id=${id}&BRSRSectionA=true`,
      );
      setSectionAData(response?.data?.data?.BRSRSectionA || null);
    } catch (error) {
      console.log(error);
      setSectionAData(null);
    } finally {
      setLoading(false);
    }
  };

  const listedIdentity = sectionAData?.listedIdentity || null;
  const productServices = sectionAData?.productServices || null;
  const operations = sectionAData?.operations || null;
  const employeesAndWorkers = sectionAData?.employeesAndWorkers || null;
  const holding = sectionAData?.holding || null;
  const transparency = sectionAData?.transparencyAndDisclosures || null;

  const safeText = (v, fallback = "N/A") => {
    if (v === null || v === undefined || v === "") return fallback;
    if (Array.isArray(v)) return v.length ? v.join(", ") : fallback;
    if (typeof v === "object") return fallback;
    return String(v);
  };

  const safeNum = (v) => {
    if (v === null || v === undefined || v === "") return "-";
    if (typeof v === "number") return v;
    return String(v);
  };

  const rows = useMemo(() => {
    if (!listedIdentity) return [];
    const qc = listedIdentity?.queryContact || {};

    return [
      {
        left: {
          no: 1,
          label: "Corporate Identity Number (CIN) of the Listed Company",
          value: safeText(listedIdentity.corporateIdentityNumber),
        },
      },
      {
        left: {
          no: 2,
          label: "Name of the Listed Company",
          value: safeText(listedIdentity.listedCompanyName),
        },
      },
      {
        left: {
          no: 3,
          label: "Year of Incorporation",
          value: safeText(listedIdentity.yearOfIncorporation),
        },
        right: {
          no: 4,
          label: "Registered Office Address",
          value: safeText(listedIdentity.registeredOfficeAddress),
        },
      },
      {
        left: {
          no: 5,
          label: "Corporate Office Address",
          value: safeText(listedIdentity.corporateOfficeAddress),
        },
      },
      {
        left: {
          no: 6,
          label: "Company E-mail ID",
          value: safeText(listedIdentity.companyEmail),
        },
      },
      {
        left: {
          no: 7,
          label: "Company Telephone Number",
          value: safeText(listedIdentity.companyTelephoneNumber),
        },
      },
      {
        left: {
          no: 8,
          label: "Company Website",
          value: safeText(listedIdentity.companyWebsite),
        },
      },
      {
        left: {
          no: 9,
          label: "Financial year for which reporting is being done",
          value: safeText(listedIdentity.financialYear),
        },
      },
      {
        left: {
          no: 10,
          label: "Name of Stock Exchange(s) where shares are listed",
          value: safeText(listedIdentity.stockExchanges),
        },
      },
      {
        left: {
          no: 11,
          label: "Paid Up Capital",
          value: safeText(listedIdentity.paidUpCapital),
        },
      },
      {
        left: {
          no: 12,
          label:
            "Name and contact details of the person who may be contacted in case of any queries on the BRSR report",
          value: `${safeText(qc.name)}, ${safeText(qc.telephone)} & ${safeText(
            qc.email,
          )}`,
        },
      },
      {
        left: {
          no: 13,
          label:
            "Reporting boundary - Are the disclosures under this report made on a standalone basis (i.e. only for the entity) or on a consolidated basis (i.e. for the entity and all the entities which form a part of its consolidated financial statements, taken together)",
          value: safeText(listedIdentity.reportingBoundary),
        },
      },
      {
        left: {
          no: 14,
          label: "Name of Assurance Provider",
          value: safeText(listedIdentity.assuranceProvider),
        },
        right: {
          no: 15,
          label: "Type of assurance obtained",
          value: safeText(listedIdentity.assuranceType),
        },
      },
    ];
  }, [listedIdentity]);

  const businessActivitiesColumns = [
    { field: "mainActivity", label: "Description of Main Activity" },
    { field: "businessActivity", label: "Description of Business Activity" },
    { field: "turnoverPercentage", label: "% of Turnover of the entity" },
  ];
  const businessActivitiesRows =
    productServices?.businessActivities?.map((x) => ({
      ...x,
      turnoverPercentage: safeNum(x?.turnoverPercentage),
    })) || [];

  const productsColumns = [
    { field: "productOrService", label: "Products/Service" },
    { field: "nicCode", label: "NIC Code" },
    { field: "turnoverPercentage", label: "% of Total Turnover Contributed" },
  ];
  const productsRows =
    productServices?.productsOrServices?.map((x) => ({
      ...x,
      turnoverPercentage: safeNum(x?.turnoverPercentage),
    })) || [];

  const locationsColumns = [
    { field: "plants", label: "Number of Plants" },
    { field: "offices", label: "Number of Offices" },
    { field: "total", label: "Total" },
  ];
  const locationsRows = operations?.numberOfLocations
    ? [
        {
          _id: "national",
          category: "National",
          plants: safeNum(operations?.numberOfLocations?.national?.plants),
          offices: safeNum(operations?.numberOfLocations?.national?.offices),
          total: safeNum(operations?.numberOfLocations?.national?.total),
        },
        {
          _id: "international",
          category: "International",
          plants: safeNum(operations?.numberOfLocations?.international?.plants),
          offices: safeNum(
            operations?.numberOfLocations?.international?.offices,
          ),
          total: safeNum(operations?.numberOfLocations?.international?.total),
        },
      ]
    : [];

  const locationsColumnsWithCategory = [
    { field: "category", label: "Location" },
    ...locationsColumns,
  ];

  const marketsColumns = [
    { field: "locationType", label: "S.No.", width: "23rem" },
    { field: "count", label: "Number" },
  ];
  console.log(operations?.marketsServed, "marketsServed");
  const marketsRows = operations?.marketsServed
    ? [
        {
          _id: "national-states",
          locationType: "National (No. of States)",
          count: safeNum(operations?.marketsServed?.nationalStates),
        },
        {
          _id: "international-countries",
          locationType: "International (No. of Countries)",
          count: safeNum(operations?.marketsServed?.internationalCountries),
        },
      ]
    : [];

  const workforceColumns = [
    { field: "particulars", label: "Particulars" },
    { field: "total", label: "Total Workers" },
    { field: "male", label: "Male(Num)" },
    { field: "malepercentage", label: "Male (%)" },
    { field: "female", label: "Female(Num)" },
    { field: "femalepercentage", label: "Female (%)" },
  ];
  const empforceColumns = [
    { field: "particulars", label: "Particulars" },
    { field: "total", label: "Total Employees" },
    { field: "male", label: "Male(Num)" },
    { field: "malepercentage", label: "Male (%)" },
    { field: "female", label: "Female(Num)" },
    { field: "femalepercentage", label: "Female (%)" },
  ];

  const makeWorkforceRows = (node) => {
    if (!node) return [];
    const mk = (label, obj) => ({
      _id: label,
      particulars: label,
      total: safeNum(obj?.total),
      male: safeNum(obj?.male),
      female: safeNum(obj?.female),
      malepercentage: safeNum(obj?.malepercentage),
      femalepercentage: safeNum(obj?.femalepercentage),
    });
    return [
      mk("Permanent", node?.permanent),
      mk("Other than Permanent", node?.other),
      mk("Total Workers", node?.total),
    ];
  };
  const makeEmpforceRows = (node) => {
    if (!node) return [];
    const mk = (label, obj) => ({
      _id: label,
      particulars: label,
      total: safeNum(obj?.total),
      male: safeNum(obj?.male),
      female: safeNum(obj?.female),
      malepercentage: safeNum(obj?.malepercentage),
      femalepercentage: safeNum(obj?.femalepercentage),
    });
    return [
      mk("Permanent", node?.permanent),
      mk("Other than Permanent", node?.other),
      mk("Total Employees", node?.total),
    ];
  };

  const empRows = makeEmpforceRows(employeesAndWorkers?.emp);
  const workerRows = makeWorkforceRows(employeesAndWorkers?.worker);

  const differentlyAbledColumns = [
    { field: "particulars", label: "Particulars" },
    { field: "total", label: "Total Workers" },
    { field: "male", label: "Male(Num)" },
    { field: "malepercentage", label: "Male (%)" },
    { field: "female", label: "Female(Num)" },
    { field: "femalepercentage", label: "Female (%)" },
  ];
  const differentlyAbledEmpColumns = [
    { field: "particulars", label: "Particulars" },
    { field: "total", label: "Total Employees" },
    { field: "male", label: "Male(Num)" },
    { field: "malepercentage", label: "Male (%)" },
    { field: "female", label: "Female(Num)" },
    { field: "femalepercentage", label: "Female (%)" },
  ];
  const diffEmpRows = makeEmpforceRows(
    employeesAndWorkers?.differentlyabled?.employees,
  );
  const diffWorkerRows = makeWorkforceRows(
    employeesAndWorkers?.differentlyabled?.workers,
  );

  const womenRepColumns = [
    { field: "particulars", label: "Particulars" },
    { field: "total", label: "Total" },
    { field: "female", label: "Female" },
    { field: "femalepercentage", label: "Female (%)" },
  ];
  const womenRepRows = employeesAndWorkers?.womenRepresentation
    ? [
        {
          _id: "bod",
          particulars: "Board of Directors",
          total: safeNum(
            employeesAndWorkers?.womenRepresentation?.boardOfDirectors?.total,
          ),
          female: safeNum(
            employeesAndWorkers?.womenRepresentation?.boardOfDirectors?.female,
          ),
          femalepercentage: safeNum(
            employeesAndWorkers?.womenRepresentation?.boardOfDirectors
              ?.femalepercentage,
          ),
        },
        {
          _id: "kmp",
          particulars: "Key Management Personnel",
          total: safeNum(
            employeesAndWorkers?.womenRepresentation?.keyManagementPersonnel
              ?.total,
          ),
          female: safeNum(
            employeesAndWorkers?.womenRepresentation?.keyManagementPersonnel
              ?.female,
          ),
          femalepercentage: safeNum(
            employeesAndWorkers?.womenRepresentation?.keyManagementPersonnel
              ?.femalepercentage,
          ),
        },
      ]
    : [];

  const turnoverColumns = [
    { field: "particulars", label: "Particulars" },
    { field: "male", label: "Male%" },
    { field: "female", label: "Female%" },
    { field: "total", label: "Total%" },
  ];
  const makeTurnoverRows = (node) => {
    if (!node) return [];
    return [
      {
        _id: "employees",
        particulars: "Permanent Employees",
        male: safeNum(node?.employees?.male),
        female: safeNum(node?.employees?.female),
        total: safeNum(node?.employees?.total),
      },
      {
        _id: "workers",
        particulars: "Permanent Workers",
        male: safeNum(node?.workers?.male),
        female: safeNum(node?.workers?.female),
        total: safeNum(node?.workers?.total),
      },
    ];
  };
  const currentYear = new Date().getFullYear();
  const turnoverCurrentRows = makeTurnoverRows(
    employeesAndWorkers?.turnoverRate?.currentfy,
  );
  const turnoverPreviousRows = makeTurnoverRows(
    employeesAndWorkers?.turnoverRate?.previousfy,
  );
  const turnoverPriorRows = makeTurnoverRows(
    employeesAndWorkers?.turnoverRate?.priorfy,
  );

  const assocColumns = [
    {
      field: "name",
      label: `Name of the holding
/ subsidiary /
associate
companies / joint
ventures (A)`,
    },
    {
      field: "relationType",
      label: `Indicate whether
holding/Subsidiary/Associate/Joint
Venture`,
    },
    {
      field: "sharePercentage",
      label: `% of shares
held by listed
entity`,
    },
    {
      field: "participatesInBR",
      label: `Does the entity indicated
at column A, participate
in the Business
Responsibility initiatives
of the listed entity?
(Yes/No)
`,
    },
  ];
  const assocRows =
    holding?.associatedCompaniesAndCsr?.map((x) => ({
      ...x,
      sharePercentage: safeNum(x?.sharePercentage),
    })) || [];

  const complaintsColumns = [
    {
      field: "stakeholder",
      label: "Stakeholder group from whom complaint is received",
    },
    {
      field: "grievanceRedressalWeblink",
      label:
        "Grievance Redressal Mechanism in Place (If Yes, then provide web-link for grievance redress policy)",
    },
    { field: "filed", label: "Number of complaints filed during the year" },
    {
      field: "pending",
      label: "Number of complaints pending resolution at close of the year",
    },
    { field: "remarks", label: "Remarks" },
  ];
  const makeComplaintsRows = (node) => {
    if (!node) return [];
    const map = [
      ["communities", "Communities"],
      ["investors", "Investors"],
      ["shareHolders", "Shareholders"],
      ["employeesAndWorkers", "Employees and Workers"],
      ["customers", "Customers"],
      ["valueChainParnter", "Value Chain Partner"],
      ["other", "Other"],
    ];
    return map.map(([key, label]) => ({
      _id: key,
      stakeholder: label,
      grievanceRedressalWeblink: safeText(
        node?.[key]?.grievanceRedressalWeblink,
        "-",
      ),
      filed: safeNum(node?.[key]?.filed),
      pending: safeNum(node?.[key]?.pending),
      remarks: safeText(node?.[key]?.remarks, "-"),
    }));
  };
  const currentComplaintsRows = makeComplaintsRows(
    transparency?.currentFYComplaints,
  );
  const previousComplaintsRows = makeComplaintsRows(
    transparency?.previousFYComplaints,
  );

  const materialIssuesColumns = [
    { field: "issueIdentify", label: "Material issue identified" },
    {
      field: "riskOrOpportunity",
      label: "Indicate whether risk or opportunity (R/O)",
    },
    {
      field: "rationale",
      label: "Rationale for identifying the risk / opportunity",
    },
    {
      field: "mitigationApproach",
      label: "In case of risk, approach to adapt or mitigate",
    },
    {
      field: "financialImplications",
      label:
        "Financial implications of the risk or opportunity (Indicate positive or negative implications)",
    },
  ];
  const materialIssuesRows =
    transparency?.materialIssues?.map((x) => ({
      ...x,
      issueIdentify: safeText(x?.issueIdentify, "-"),
      riskOrOpportunity: safeText(x?.riskOrOpportunity, "-"),
      rationale: safeText(x?.rationale, "-"),
      mitigationApproach: safeText(x?.mitigationApproach, "-"),
      financialImplications: safeText(x?.financialImplications, "-"),
    })) || [];

  return (
    <div className={styles.sectionA}>
      <div className={styles.topRow}>
        <div
          className={`${styles.title} section-heading-common common_text_style`}
        >
          SECTION A: GENERAL DISCLOSURE
        </div>
      </div>

      <table className={styles.table}>
        <thead>
          <tr>
            <th
              colSpan={2}
              className={`${styles.cellBase} ${styles.headerRow}`}
            >
              I. DETAILS OF THE LISTED ENTITY.
            </th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td className={styles.cellBase} colSpan={2}>
                Loading...
              </td>
            </tr>
          ) : rows.length ? (
            rows.map((r, idx) => {
              const left = r.left;
              const right = r.right;

              if (right) {
                return (
                  <tr key={idx}>
                    <td className={`${styles.cellBase} ${styles.leftCell}`}>
                      <span className={styles.qNo}>{left.no}. </span>
                      <span className={styles.qLabel}>{left.label}: </span>
                      <span className={styles.qValue}>{left.value}</span>
                    </td>
                    <td className={`${styles.cellBase} ${styles.rightCell}`}>
                      <span className={styles.qNo}>{right.no}. </span>
                      <span className={styles.qLabel}>{right.label}: </span>
                      <span className={styles.qValue}>{right.value}</span>
                    </td>
                  </tr>
                );
              }

              return (
                <tr key={idx}>
                  <td className={styles.cellBase} colSpan={2}>
                    <span className={styles.qNo}>{left.no}. </span>
                    <span className={styles.qLabel}>{left.label}: </span>
                    <span className={styles.qValue}>{left.value}</span>
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td className={styles.cellBase} colSpan={2}>
                No data found.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className={styles.subSectionSpacer} />

      <div className={styles.subSectionHeading}>II. PRODUCTS/SERVICES</div>
      <ReportTable
        title="16. Details of Business Activities (accounting for 90% of the turnover)"
        columns={businessActivitiesColumns}
        rows={businessActivitiesRows}
      />
      <ReportTable
        title="17. Products/Services sold by the entity (accounting for 90% of the entity's turnover)"
        columns={productsColumns}
        rows={productsRows}
      />

      <div className={styles.subSectionHeading}>III. OPERATIONS</div>
      <ReportTable
        title="18. Number of locations where plants and/or operations/offices of the entity are situated"
        columns={locationsColumnsWithCategory}
        rows={locationsRows}
      />
      <ReportTable
        title="19. Markets served by the entity"
        subTitle="a. Number of locations:"
        columns={marketsColumns}
        rows={marketsRows}
        showSerialNumber={false}
      />
      <div className={styles.qaBlock}>
        <div className={styles.qaQuestion}>
          b. What is the contribution of exports as a percentage of the total
          turnover of the entity?
        </div>
        <div className={styles.qaAnswer}>
          {safeText(operations?.marketsServed?.exportsContribution, "-")}
        </div>

        <div className={styles.qaQuestion}>
          c. A brief on types of customers
        </div>
        <div className={styles.qaAnswer}>
          {safeText(operations?.marketsServed?.customerTypes, "-")}
        </div>
      </div>

      <div className={styles.subSectionHeading}>IV. EMPLOYEES</div>
      <ReportTable
        title="20. Details at the end of Financial Year"
        subTitle="a. Employees and workers (including differently abled):"
        columns={empforceColumns}
        rows={empRows}
        showSerialNumber={false}
        helperText="Employees"
      />
      {/* <div className={styles.miniLabel}>Employees</div> */}
      {/* <ReportTable
        columns={workforceColumns}
        rows={empRows}
        showSerialNumber={false}
      /> */}
      <div className={styles.miniLabel}>Workers</div>
      <ReportTable
        columns={workforceColumns}
        rows={workerRows}
        showSerialNumber={false}
      />

      <div className={styles.qaQuestion}>
        b. Differently abled Employees and workers:
      </div>
      <ul className={styles.bulletBlock}>
        <li>Differently Abled Employees</li>
      </ul>
      <ReportTable
        columns={differentlyAbledEmpColumns}
        rows={diffEmpRows}
        showSerialNumber={false}
      />
      <ul className={styles.bulletBlock}>
        <li>Differently Abled Workers</li>
      </ul>
      <ReportTable
        columns={differentlyAbledColumns}
        rows={diffWorkerRows}
        showSerialNumber={false}
      />
      <ReportTable
        title="21. Participation/Inclusion/Representation of Women"
        columns={womenRepColumns}
        rows={womenRepRows}
        showSerialNumber={false}
      />
      <ReportTable
        title={`22. Turnover rate for permanent employees and workers (Disclose trends for the past 3 years)`}
        subTitle={`a. Turnover rate in current FY (${currentYear - 1} - ${currentYear})`}
        columns={turnoverColumns}
        rows={turnoverCurrentRows}
        showSerialNumber={false}
      />
      <ReportTable
        subTitle={`b. Turnover rate in previous FY (${currentYear - 2} - ${
          currentYear - 1
        })`}
        columns={turnoverColumns}
        rows={turnoverPreviousRows}
        showSerialNumber={false}
      />
      <ReportTable
        subTitle={`c. Turnover rate in the year prior to the previous FY (${
          currentYear - 3
        } - ${currentYear - 2})`}
        columns={turnoverColumns}
        rows={turnoverPriorRows}
        showSerialNumber={false}
      />

      <div className={styles.subSectionHeading}>
        V. HOLDING, SUBSIDIARY & ASSOCIATE COMPANIES AND CSR DETAILS
      </div>
      <ReportTable
        title="23. Names of Holding/Subsidiary/Associate Companies/Joint Ventures"
        columns={assocColumns}
        rows={assocRows}
      />

      <div className={styles.subSectionHeading}>VI. CSR DETAILS</div>
      <div className={styles.csrInputBlock}>
        <div className={styles.csrQuestion}>
          24. a. Whether CSR is applicable as per section 135 of Companies Act,
          2013
        </div>
        <div className={styles.csrAnswer}>
          {holding?.csrDetails?.applicable ? "Yes" : "No"}
        </div>
        {holding?.csrDetails?.applicable && (
          <div className={styles.csrValueRow}>
            <div className={styles.csrValueItem}>
              <div className={styles.csrValueLabel}>b. Turnover (INR)</div>
              <div className={styles.csrValueText}>
                {safeNum(holding?.csrDetails?.turnover)}
              </div>
            </div>
            <div className={styles.csrValueItem}>
              <div className={styles.csrValueLabel}>c. Net Worth (INR)</div>
              <div className={styles.csrValueText}>
                {safeNum(holding?.csrDetails?.netWorth)}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className={styles.subSectionHeading}>
        VII. TRANSPARENCY AND DISCLOSURES
      </div>
      <div className={styles.materialHeading}>
        25.Complaints/Grievances on any of the principles (Principles 1 to 9)
        under the National Guidelines on Responsible Business Conduct:
      </div>
      <ul className={styles.bulletBlock}>
        <li>
          {brsrFyCurrent
            && `In FY ${brsrFyCurrent} (Current Financial Year)`
       }
        </li>
      </ul>
      <ReportTable
        columns={complaintsColumns}
        rows={currentComplaintsRows}
        showSerialNumber={false}
      />
      <ul className={styles.bulletBlock}>
        <li>
          {brsrFyPrevious
            && `In FY ${brsrFyPrevious} (Previous Financial Year)`
          }
        </li>
      </ul>
      <ReportTable
        columns={complaintsColumns}
        rows={previousComplaintsRows}
        showSerialNumber={false}
      />
      <div className={styles.materialHeading}>
        26.Overview of the entity's material responsible business conduct issues
      </div>
      <div className={styles.materialDescription}>
        Please indicate material responsible business conduct and sustainability
        issues pertaining to environmental and social matters that present a
        risk or an opportunity to your business, rationale for identifying the
        same, approach to adapt or mitigate the risk along-with its financial
        implications, as per the following format.
      </div>
      <ReportTable columns={materialIssuesColumns} rows={materialIssuesRows} />
    </div>
  );
};

export default SectionA;
